import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import axios from 'axios';
import api from '../utils/apiInterceptor';
import { API_URL } from '../constants/data';
import { fetchAllCategoriesThunk } from '../features/categories/categoriesThunk';

import FilterPanel from '../components/FilterPanel';
import SearchHistory from '../components/SearchHistory';
import { fetchCategoryAlerts } from '../features/alerts/categoryAlertSlice';

import { useFilters } from '../hooks/useFilters';
import { useApiData } from '../hooks/useApiData';
import { useStateData } from '../hooks/useStateData';
import { useSearchHistory } from '../hooks/useSearchHistory';
import { useDashboardHandler } from '../hooks/useDashboardHandler';
import { useDashboardHistoryHandler } from '../hooks/useDashboardHistoryHandler';
import { useApplyDashboardFilter } from '../hooks/useApplyDashboardFilter';
import AlertComponent from '../components/AlertComponent';
import ProportionalBlocks from '../components/ProprotionalBlocks';
import { useDashboardInitialization } from '../hooks/useDashboardInitialization';
import CategoryBarChart from '../components/CategoryBarChart';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';


const Dashboard = () => {
    const [categories, setCategories] = useState({});
    const [categoryAlertData, setCategoryAlertData] = useState([]);
    const [companyData, setCompanyData] = useState([]);
    const [stateData, setStateData] = useState([]);

    const [currentSubcategoryPrompt, setCurrentSubcategoryPrompt] = useState('');

    // Loading state management
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [loadingMessage, setLoadingMessage] = useState('Loading...');

    // Granular loading states
    const [isCategoriesLoading, setIsCategoriesLoading] = useState(true);
    const [isAlertsLoading, setIsAlertsLoading] = useState(true);
    const [isCompaniesLoading, setIsCompaniesLoading] = useState(true);
    const [isStatesLoading, setIsStatesLoading] = useState(true);

    const { defaultStateData } = useStateData();
    const { filters, handleFilterChange, updateFilters } = useFilters();
    const { searchHistory, saveToHistory, clearHistory, deleteHistoryItem, selectedHistoryIndex, setSelectedHistoryIndex } = useSearchHistory();
    const {
        totalCounts,
        setTotalCounts,
        isLoading,
        setIsLoading,
        fetchCategoryWithCount,
        fetchSemanticRCA,
        fetchSemanticRCA2,
        fetchUserData,
        fetchSpatialData,
        fetchAllCompanyDetails,
        fetchCategoryAlert,
        fetchAllTypeCompanyDetails
    } = useApiData(defaultStateData);

    const { reinitializeAllData, cancelInitialization } = useDashboardInitialization(
        setCategories,
        setCategoryAlertData,
        setCompanyData,
        setStateData,
        setTotalCounts,
        setIsInitialLoading,
        setLoadingProgress,
        setLoadingMessage,
        setIsLoading,
        fetchCategoryAlert,
        fetchSpatialData,
        setIsCategoriesLoading,
        setIsAlertsLoading,
        setIsCompaniesLoading,
        setIsStatesLoading
    );

    const [dividerPos, setDividerPos] = useState(55);
    const containerRef = useRef(null);
    const isDragging = useRef(false);
    const [pbReady, setPbReady] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpand = () => {
        // setIsExpanded(prev => !prev);
    };

    const getAccessToken = () => {
        return localStorage.getItem("access_token") || "";
    }

    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        isDragging.current = true;
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';
    }, []);

    const handleMouseUp = useCallback(() => {
        isDragging.current = false;
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (!isDragging.current || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const newPos = ((e.clientX - rect.left) / rect.width) * 100;
        const constrainedPos = Math.max(20, Math.min(80, newPos));
        setDividerPos(constrainedPos);
    }, []);

    useEffect(() => {
        const handleResize = () => {
            const w = window.innerWidth;
            if (w < 900) {
                setDividerPos(prev => Math.min(prev, 42));
            } else if (w < 1000) {
                setDividerPos(36);
            } else if (w < 1200) {
                setDividerPos(38);
            } else if (w < 1440) {
                setDividerPos(42);
            } else if (w < 1680) {
                setDividerPos(48);
            } else {
                setDividerPos(55);
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const cleanup = () => {
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
            isDragging.current = false;
        };

        const globalMouseUp = () => {
            if (isDragging.current) {
                handleMouseUp();
            }
        };

        document.addEventListener('mouseup', globalMouseUp);
        window.addEventListener('beforeunload', cleanup);

        return () => {
            document.removeEventListener('mouseup', globalMouseUp);
            window.removeEventListener('beforeunload', cleanup);
            cleanup();
        };
    }, [handleMouseUp]);

    useEffect(() => {
        setPbReady(false);
    }, [isLoading, categories]);

    const fetchCategories = async (categoryData, currentFilters = filters) => {
        try {
            const { updatedData, totalCount } = await fetchCategoryWithCount(
                categoryData,
                setCategories,
                currentFilters
            );
            setCategories(updatedData);
            setTotalCounts(totalCount);
            return updatedData;
        } catch (error) {
            console.error('fetchCategoryWithCount failed:', error);
            return null;
        }
    };

    const fethAllCompanies = useCallback(async (categoryData, complaintNumbers, currentFilters = filters) => {
        try {
            const updatedFilters = {
                ...currentFilters,
                query: categoryData[1].prompt,
                complaintNumbers: complaintNumbers
            };

            const data = await fetchAllCompanyDetails(updatedFilters, setCompanyData);
            setCompanyData(data);

            return data;
        } catch (error) {
            setCompanyData([]);
            return [];
        }
    }, [filters, fetchAllCompanyDetails]);

    const fetchAllTypeCompaniesData = useCallback(async (categoryData, complaintNumbers, currentFilters = filters) => {
        try {
            const updatedFilters = {
                ...currentFilters,
                query: categoryData[1].prompt,
                complaintNumbers: complaintNumbers
            };

            const data = await fetchAllTypeCompanyDetails(updatedFilters, setCompanyData);
            setCompanyData(data);

            return data;
        } catch (error) {
            setCompanyData({ converged: [], non_converged: [] });
            return { converged: [], non_converged: [] };
        }
    }, [filters, fetchAllTypeCompanyDetails]);

    const fetchAllAlerts = useCallback(async (complaintNumbers, categories = null, currentFilters = filters) => {
        try {
            const updateFilters = {
                ...currentFilters,
                complaintNumbers: complaintNumbers
            }

            // If we have categories (object from fetchCategories), get keys (names)
            let categoryNames = null;
            if (categories && typeof categories === 'object') {
                categoryNames = Object.keys(categories);
            } else if (Array.isArray(categories)) {
                categoryNames = categories;
            }

            const data = await fetchCategoryAlert(updateFilters, categoryNames);
            setCategoryAlertData(data);
            return data;
        } catch (error) {
            setCategoryAlertData([]);
            return [];
        }
    }, [fetchCategoryAlert, filters])

    const fetchAllStatesData = useCallback(async (categoryData, complaintNumbers, currentFilters = filters) => {
        try {
            const updatedFilters = {
                ...currentFilters,
                query: categoryData[1].prompt,
                complaintNumbers: complaintNumbers
            };
            const data = await fetchSpatialData(updatedFilters, setStateData);
            setStateData(data);
            return data;
        } catch (error) {
            setStateData([]);
            return [];
        }
    }, [filters, fetchSpatialData]);

    const { handleDashboardClick } = useDashboardHandler({
        categories,
        filters,
        handleFilterChange,
        fetchSemanticRCA2,
        fetchSpatialData,
        fethAllCompanies,
        fetchAllAlerts,
        fetchUserData,
        fetchCategories,
        saveToHistory,
        setIsLoading,
        companyData,
        setTotalCounts,
        setCurrentSubcategoryPrompt,
        fetchAllStatesData,
        setIsCategoriesLoading,
        setIsAlertsLoading,
        setIsCompaniesLoading,
        setIsStatesLoading,
        fetchAllTypeCompaniesData
    });

    const { handleDashboardHistoryClick } = useDashboardHistoryHandler({
        filters,
        updateFilters,
        fetchSemanticRCA2,
        fetchCategories,
        setTotalCounts,
        setIsLoading,
        fetchAllCompanyDetails,
        fethAllCompanies,
        fetchAllAlerts,
        fetchSpatialData,
        fetchAllStatesData,
        setIsCategoriesLoading,
        setIsAlertsLoading,
        setIsCompaniesLoading,
        setIsStatesLoading,
        fetchAllTypeCompaniesData
    });

    const { handleApplyDashboardFilter } = useApplyDashboardFilter({
        filters,
        updateFilters,
        fetchSemanticRCA2,
        fetchUserData,
        fetchSpatialData,
        fetchAllCompanyDetails,
        fethAllCompanies,
        fetchAllAlerts,
        fetchCategories,
        saveToHistory,
        totalCounts,
        stateData,
        setTotalCounts,
        setIsLoading,
        fetchAllStatesData,
        setIsCategoriesLoading,
        setIsAlertsLoading,
        setIsCompaniesLoading,
        setIsStatesLoading,
        fetchAllTypeCompaniesData
    });

    const formatCategories = (categoriesObj) => {
        if (!categoriesObj || typeof categoriesObj !== 'object') return [];
        return Object.entries(categoriesObj).map(([key, value]) => ({
            categoryName: key,
            counts: value.count || 0,
        }));
    };

    const categoriesData = formatCategories(categories);

    useEffect(() => {
        reinitializeAllData(filters);

        return () => {
            cancelInitialization();
        };
    }, []);

    const clearAllCache = () => {
        const CATEGORY_COUNT = "categoryDataCache";
        const ALERT_DATA = "alertDataCache";
        const STATE_DATA = "stateDataCache";
        const COMPANY_DATA = "companyDataCache";

        localStorage.removeItem(CATEGORY_COUNT);
        localStorage.removeItem(ALERT_DATA);
        localStorage.removeItem(STATE_DATA);
        localStorage.removeItem(COMPANY_DATA);
        localStorage.removeItem("currentSubcategoryData");
        localStorage.removeItem("subAlertCache");
        localStorage.removeItem("subStateDataCache");
        localStorage.removeItem("subCompanyCache");
    };

    const handleApplyFiltersWithoutQuery = useCallback(async () => {
        if (filters.query !== "" && filters.query !== undefined) {
            return;
        }

        let cancelled = false;

        const CATEGORY_COUNT = "categoryDataCache";
        const ALERT_DATA = "alertDataCache";
        const STATE_DATA = "stateDataCache";
        const COMPANY_DATA = "companyDataCache";
        const CACHE_EXPIRY_HOURS = 24;

        const isCacheValid = (cacheTimestamp) => {
            if (!cacheTimestamp) return false;
            const now = Date.now();
            const age = (now - cacheTimestamp) / (1000 * 60 * 60);
            return age < CACHE_EXPIRY_HOURS;
        };

        clearAllCache();

        const fetchAllCategories = async () => {
            try {
                setIsCategoriesLoading(true);
                const start_date = filters.startDate ? new Date(filters.startDate).toISOString().split('T')[0] : new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split('T')[0];
                const end_date = filters.endDate ? new Date(filters.endDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];


                const res = await api.post(`${API_URL}/complaint_distribution_by_es`, {
                    start_date: start_date,
                    end_date: end_date,
                    stateName: "All",
                    sectorName: "All",
                    categoryName: "All",
                    attribute: "sectorName",
                    skip: 0,
                    limit: 50,
                })

                const data = res.data.distribution || [];
                let totalComplaints = 0;
                const baseCategories = data.reduce((acc, item) => {
                    if (item.key != null && item.key !== "null") {
                        acc[item.key] = {
                            prompt: item.categoryPrompt || "",
                            count: item.doc_count,
                            complaintNumbers: ["NA"]
                        }
                    }
                    totalComplaints += item.doc_count;
                    return acc;
                }, {})

                setCategories(baseCategories)
                setTotalCounts(totalComplaints)
                localStorage.setItem(
                    CATEGORY_COUNT,
                    JSON.stringify({
                        timestamp: Date.now(),
                        data: {
                            categories: baseCategories,
                            totalComplaints,
                        }
                    })
                )
            } catch (error) {
                console.error("Error fetching all categories:", error)
            } finally {
                setIsCategoriesLoading(false);
            }
        }

        const fetchAllCategoryAlerts = async () => {
            try {
                setIsAlertsLoading(true);
                const startDate = filters.startDate
                    ? new Date(filters.startDate).toISOString().split('T')[0]
                    : new Date().toISOString().split('T')[0];
                const response = await api.post(
                    `${API_URL}/get_category_alert`,
                    {
                        last_days: 84,
                        given_date: startDate,
                        value: filters?.value || 1,
                        CityName: filters?.CityName || "All",
                        stateName: filters?.stateName || "All",
                        complaintType: filters?.complaintType || "All",
                        complaintMode: filters?.complaintMode || "All",
                        companyName: filters?.companyName || "All",
                        complaintStatus: filters?.complaintStatus || "All",
                        threshold: filters?.threshold || 1.3,
                        complaint_numbers: ["NA"],
                    }
                );

                const data = response.data || [];
                if (cancelled) return;
                setCategoryAlertData(data);

                localStorage.setItem(
                    ALERT_DATA,
                    JSON.stringify({
                        timestamp: Date.now(),
                        data,
                    })
                );
            } catch (error) {
                console.error("Error fetching all category alerts:", error);
            } finally {
                setIsAlertsLoading(false);
            }
        };

        const fetchAllStates = async () => {
            try {
                setIsStatesLoading(true);
                const start_date = filters.startDate ? new Date(filters.startDate).toISOString().split('T')[0] : new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split('T')[0];
                const end_date = filters.endDate ? new Date(filters.endDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];

                const response = await api.post(`${API_URL}/complaint_distribution_by_es`,
                    {
                        start_date: start_date,
                        end_date: end_date,
                        stateName: "All",
                        sectorName: "All",
                        categoryName: "All",
                        attribute: "stateName",
                        skip: 0,
                        limit: 50,
                    }
                )
                const data = response.data.distribution || [];

                setStateData(data);
                localStorage.setItem(
                    STATE_DATA,
                    JSON.stringify({
                        timestamp: Date.now(),
                        data: data,
                    })
                )
            } catch (error) {
                console.error("Error fetching all states:", error);
            } finally {
                setIsStatesLoading(false);
            }
        };

        const loadInitialCompanyData = async () => {
            try {
                setIsCompaniesLoading(true);

                // Check if there's a query from subcategory cache
                let effectiveQuery = filters.query || '';
                let effectiveComplaintNumbers = filters.complaint_numbers || ['NA'];

                if (!effectiveQuery) {
                    try {
                        const subCatCached = JSON.parse(localStorage.getItem("currentSubcategoryData") || "{}");
                        if (subCatCached?.prompt && subCatCached?.timestamp) {
                            const age = (Date.now() - subCatCached.timestamp) / (1000 * 60 * 60);
                            if (age < CACHE_EXPIRY_HOURS) {
                                effectiveQuery = subCatCached.query || subCatCached.prompt;
                                if (subCatCached.complaintNumbers) {
                                    effectiveComplaintNumbers = subCatCached.complaintNumbers;
                                }
                            }
                        }
                    } catch (e) {
                        console.warn("Failed reading subcategory cache:", e);
                    }
                }

                const hasQuery = effectiveQuery && effectiveQuery.trim() !== '';

                // Check appropriate cache first
                if (hasQuery) {
                    const subCached = JSON.parse(localStorage.getItem("subCompanyCache") || "{}");
                    if (subCached?.timestamp && isCacheValid(subCached.timestamp) &&
                        (subCached?.query === effectiveQuery || subCached?.prompt === effectiveQuery)) {
                        setCompanyData(subCached.data);
                        setIsCompaniesLoading(false);
                        return;
                    }
                } else {
                    const cached = JSON.parse(localStorage.getItem(COMPANY_DATA) || "{}");
                    if (cached?.timestamp && isCacheValid(cached.timestamp)) {
                        setCompanyData(cached.data);
                        setIsCompaniesLoading(false);
                        return;
                    }
                }

                const start_date = filters.startDate
                    ? new Date(filters.startDate).toISOString().split("T")[0]
                    : new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split("T")[0];

                const end_date = filters.endDate
                    ? new Date(filters.endDate).toISOString().split("T")[0]
                    : new Date().toISOString().split("T")[0];

                const response = await api.post(
                    `${API_URL}/get_all_company_distribution`,
                    {
                        query: effectiveQuery,
                        start_date: start_date,
                        end_date: end_date,
                        companyName: "All",
                        purchaseCity: 'All',
                        complaintMode: 'All',
                        complaintStatus: 'All',
                        stateName: 'All',
                        threshold: 1.3,
                        complainNumbers: filters.complaint_numbers || ["NA"],
                        skip: 0,
                        size: 25,
                    },
                );

                const results = response.data

                setCompanyData(results);

                const cacheKey = hasQuery ? "subCompanyCache" : COMPANY_DATA;
                const cacheData = {
                    timestamp: Date.now(),
                    data: results,
                };
                if (hasQuery) {
                    cacheData.query = effectiveQuery;
                }
                localStorage.setItem(cacheKey, JSON.stringify(cacheData));
            } catch (error) {
                console.error("Error loading initial company data:", error);
                setCompanyData([]);
            } finally {
                setIsCompaniesLoading(false);
            }
        };

        const runAllAPIs = async () => {
            try {
                if (setIsLoading) setIsLoading(true);
                await Promise.all([
                    fetchAllCategories(),
                    fetchAllCategoryAlerts(),
                    fetchAllStates(),
                    loadInitialCompanyData(),
                ]);
            } catch (error) {
                console.error("Error running all APIs:", error);
            } finally {
                if (setIsLoading) setIsLoading(false);
            }
        };

        runAllAPIs();

        return () => {
            cancelled = true;
        };
    }, [filters, setIsLoading, setCategories, setTotalCounts, setCategoryAlertData, setStateData, setCompanyData]);

    const handleApply = () => {
        handleFilterChange();
        if (filters.query === "" || filters.query === undefined) {
            handleApplyFiltersWithoutQuery();
        }
    };

    useEffect(() => {
        try {
            localStorage.setItem('dashboardFilters', JSON.stringify({ ...filters, complaint_numbers: ["NA"], complaintNumbers: ["NA"] }));
        } catch (error) {
            console.error('Error saving filters to localStorage:', error);
        }
    }, [filters]);

    const handleClearHistory = useCallback(() => {
        clearHistory();
        setCurrentSubcategoryPrompt('');
        updateFilters({
            query: '',
            title: '',
            value: 1,
            complaintNumbers: ["NA"],
            complaint_numbers: ["NA"]
        });
        setStateData([]);
        localStorage.removeItem('dashboardFilters');
        setTimeout(() => {
            reinitializeAllData(filters);
        }, 100);
    }, [clearHistory, updateFilters, setStateData, reinitializeAllData, filters]);

    const totalComplaints = categories
        ? Object.values(categories).reduce((sum, cat) => sum + (cat.count || 0), 0)
        : 0;

    const totalHighAlerts = categoryAlertData?.filter(
        (item) => Number(item.increase_percentage) > 20
    ).length || 0;

    const totalCategories = Object.keys(categories).length;

    useEffect(() => {
        if (searchHistory?.length > 0) {
            const lastQuery = searchHistory[searchHistory.length - 1];
            if (lastQuery?.params) {
                updateFilters(lastQuery.params);
            }
        }
    }, [searchHistory]);

    const SimpleLoadingOverlay = ({ show, onAlertComponent = false, onProportionalBlock = false }) => {
        if (!show) return null;

        return (
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999,
                    gap: 2,
                    pointerEvents: 'auto'
                }}
            >
                <Box
                    sx={{
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        border: '4px solid #e0e0e0',
                        borderTop: '4px solid rgba(94, 79, 170, 1)',
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' }
                        }
                    }}
                />
                <Typography sx={{ fontSize: '14px', color: '#666', fontWeight: 500 }}>
                    {loadingMessage}
                </Typography>
            </Box>
        );
    };

    const hasData = categories && (Array.isArray(categories) ? categories.length > 0 : Object.keys(categories).length > 0);

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            width: '100%',
            overflow: 'auto',
            backgroundColor: '#ECEAFC',
            position: 'relative',
        }}>
            {/* Header Section - Always Visible */}
            <Box sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1000,
                px: { xs: 1.5, sm: 2, md: 2.5 },
                pt: { xs: 1, sm: 1.5 },
                pb: 0,
                backgroundColor: '#ECEAFC',
                flexShrink: 0,
            }}>
                <SearchHistory
                    searchHistory={searchHistory}
                    onHistoryClick={handleDashboardHistoryClick}
                    onClearHistory={handleClearHistory}
                    onDeleteHistoryItem={deleteHistoryItem}
                    selectedHistoryIndex={selectedHistoryIndex}
                    setSelectedHistoryIndex={setSelectedHistoryIndex}
                />
                <FilterPanel
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onApplyFilters={filters?.query === "" ? handleApply : handleApplyDashboardFilter}
                    isLoading={isLoading}
                    searchHistory={searchHistory}
                    clearHistory={handleClearHistory}
                />
            </Box>

            {/* Main Content Area */}
            <Box
                ref={containerRef}
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    px: { xs: 1, sm: 2 },
                    pt: { xs: 1, sm: 1 },
                    pb: { xs: 1, sm: 2 },
                    flex: 1,
                    position: 'relative',
                    overflow: 'visible',
                    minHeight: 0,
                    gap: { xs: 1.5, md: 0 },
                }}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            >
                <Box sx={{
                    // width: isExpanded ? '98.92%' : { xs: '100%', md: `${dividerPos}%` },
                    // minWidth: isExpanded ? '98.92%' : { md: '280px' },
                    // maxWidth: isExpanded ? '100%' : { md: '75%' },
                    // overflow: 'hidden',
                    // pr: { xs: 0, md: 1.5 },
                    // transition: isDragging.current ? 'none' : 'width 0.3s ease',
                    // display: 'flex',
                    // flexDirection: 'column',
                    // height: '100%',
                    // minHeight: { xs: 440, sm: 480 },
                    // position: 'relative',

                    width: { xs: '100%', md: `${dividerPos}%` },
                    minWidth: { md: '280px' },
                    maxWidth: { md: '75%' },
                    overflow: 'hidden',
                    pr: { xs: 0, md: 1.5 },
                    transition: isDragging.current ? 'none' : 'width 0.1s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    minHeight: { xs: 440, sm: 480 },
                    position: 'relative'
                }}>
                    <CategoryBarChart
                        Data={categories}
                        totalCounts={totalCounts || totalComplaints}
                        onDataPointClick={handleDashboardClick}
                        isLoading={Boolean(isLoading) || (!pbReady && hasData)}
                        containerWidth={dividerPos}
                        onReady={useCallback(() => setPbReady(true), [])}
                        showAll={isExpanded}
                    />
                    <SimpleLoadingOverlay show={isInitialLoading} onProportionalBlock={true} />
                </Box>

                {/* Divider */}
                <Box
                    sx={{
                        display: { xs: 'none', md: 'flex' },
                        width: '12px',
                        cursor: 'col-resize',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10,
                        mx: 0.5,
                        position: 'relative',
                        transition: 'all 0.2s ease',
                        flexShrink: 0
                    }}
                >
                    {/* Vertical Line */}
                    <Box
                        onMouseDown={handleMouseDown}
                        sx={{
                            width: '4px',
                            height: '100%',
                            backgroundColor: isDragging.current ? '#2196F3' : '#e0e0e0',
                            borderRadius: '4px',
                            transition: isDragging.current ? 'none' : 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: isDragging.current ? '#2196F3' : '#bdbdbd'
                            }
                        }}
                    />

                    {/* Expand/Collapse Button */}
                    {/* <Box
                        onClick={toggleExpand}
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '24px',
                            height: '40px',
                            backgroundColor: '#fff',
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            zIndex: 20,
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                                borderColor: '#d0d0d0'
                            }
                        }}
                    >
                        {isExpanded ?
                            <ChevronLeft sx={{ fontSize: 20, color: '#666' }} /> :
                            <ChevronRight sx={{ fontSize: 20, color: '#666' }} />
                        }
                    </Box> */}
                </Box>

                {/* Alert Component Section */}
                <Box sx={{
                    // width: isExpanded ? '0px' : { xs: '100%', md: `${100 - dividerPos}%` },
                    // minWidth: isExpanded ? '0px' : { md: '280px' },
                    // overflow: 'hidden',
                    // pl: { xs: 0, md: isExpanded ? 0 : 1.5 },
                    // transition: isDragging.current ? 'none' : 'width 0.3s ease, padding 0.3s ease',
                    // height: '100%',
                    // display: isExpanded ? 'none' : 'flex',
                    // flexDirection: 'column',
                    // position: 'relative',
                    // opacity: isExpanded ? 0 : 1,
                    // pt: 1,
                    // mb: 2
                    width: { xs: '100%', md: `${100 - dividerPos}%` },
                    minWidth: { md: '280px' },
                    overflow: 'visible',
                    pl: { xs: 0, md: 1.5 },
                    transition: isDragging.current ? 'none' : 'width 0.1s ease',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative'
                }}>
                    <AlertComponent
                        totalCount={totalCounts || totalComplaints}
                        categoriesCount={totalCategories}
                        categoryData={categoriesData}
                        loading={Boolean(isLoading) || (!pbReady && hasData)}
                        highAlertCount={totalHighAlerts}
                        companyData={companyData}
                        alertData={categoryAlertData}
                        stateData={stateData}
                        currentQuery={filters.query}
                        filters={filters}
                        categoriesLoading={isCategoriesLoading}
                        alertsLoading={isAlertsLoading}
                        companiesLoading={isCompaniesLoading}
                        statesLoading={isStatesLoading}
                    />
                    {/* <SimpleLoadingOverlay show={isInitialLoading} onAlertComponent={true} /> */}
                </Box>
            </Box>
        </Box>
    );
};

export default Dashboard;